import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "../components/DashboardLayout";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  TrendingUp, TrendingDown, Upload, Trash2, FileText,
  RefreshCw, AlertCircle, ChevronRight, Loader2,
  DollarSign, Receipt, PiggyBank, BarChart2, Bot,
  Calendar, Filter, Search, X, CheckCircle, Info
} from "lucide-react";

const TABS = ["Overview", "Transactions", "Import", "BAS Advisor"];
const CATEGORY_COLORS = [
  "#38bdf8", "#34d399", "#fb923c", "#a78bfa",
  "#f472b6", "#facc15", "#60a5fa", "#4ade80",
  "#f87171", "#94a3b8",
];

const fmt = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n || 0);
const fmtDecimal = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 }).format(n || 0);

function StatCard({ label, value, sub, icon: Icon, color = "sky" }) {
  const styles = {
    sky: { border: "1px solid rgba(56,189,248,0.3)", background: "linear-gradient(135deg,rgba(56,189,248,0.15),rgba(56,189,248,0.03))", iconColor: "#38bdf8" },
    green: { border: "1px solid rgba(52,211,153,0.3)", background: "linear-gradient(135deg,rgba(52,211,153,0.15),rgba(52,211,153,0.03))", iconColor: "#34d399" },
    red: { border: "1px solid rgba(248,113,113,0.3)", background: "linear-gradient(135deg,rgba(248,113,113,0.15),rgba(248,113,113,0.03))", iconColor: "#f87171" },
    purple: { border: "1px solid rgba(167,139,250,0.3)", background: "linear-gradient(135deg,rgba(167,139,250,0.15),rgba(167,139,250,0.03))", iconColor: "#a78bfa" },
    amber: { border: "1px solid rgba(251,191,36,0.3)", background: "linear-gradient(135deg,rgba(251,191,36,0.15),rgba(251,191,36,0.03))", iconColor: "#fbbf24" },
  };
  const s = styles[color] || styles.sky;
  return (
    <div style={{ borderRadius: 12, border: s.border, background: s.background, padding: "20px" }} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
        <p style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{label}</p>
        {Icon && <Icon style={{ width: 18, height: 18, color: s.iconColor, opacity: 0.8 }} />}
      </div>
      <p style={{ fontSize: 22, fontWeight: 700, color: "#f1f5f9", letterSpacing: "-0.02em" }}>{value}</p>
      {sub && <p style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{sub}</p>}
    </div>
  );
}

// ============= OVERVIEW TAB =============
function OverviewTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ start: "", end: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.start) params.start_date = filter.start;
      if (filter.end) params.end_date = filter.end;
      const res = await axios.get(`${API}/life/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load overview");
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;

  const { summary = {}, category_breakdown = [], monthly_data = [] } = data || {};
  const netPositive = (summary.net_cashflow || 0) >= 0;

  return (
    <div className="space-y-6">
      {/* Date filter */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex gap-2 items-center rounded-lg px-3 py-2" style={{ background: "#1e293b", border: "1px solid #334155" }}>
          <Calendar className="w-4 h-4" style={{ color: "#94a3b8" }} />
          <input type="date" value={filter.start} onChange={e => setFilter(f => ({ ...f, start: e.target.value }))}
            style={{ background: "transparent", fontSize: 13, color: "#e2e8f0", outline: "none", width: "128px", colorScheme: "dark" }} />
          <span style={{ color: "#64748b" }}>—</span>
          <input type="date" value={filter.end} onChange={e => setFilter(f => ({ ...f, end: e.target.value }))}
            style={{ background: "transparent", fontSize: 13, color: "#e2e8f0", outline: "none", width: "128px", colorScheme: "dark" }} />
        </div>
        <button onClick={load} style={{ display: "flex", alignItems: "center", gap: 8, background: "#0284c7", color: "#fff", padding: "8px 16px", borderRadius: 8, fontSize: 13, border: "none", cursor: "pointer" }}>
          <Filter className="w-4 h-4" /> Apply
        </button>
        {(filter.start || filter.end) && (
          <button onClick={() => { setFilter({ start: "", end: "" }); }} style={{ color: "#94a3b8", fontSize: 13, display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer" }}>
            <X className="w-4 h-4" /> Clear
          </button>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Income" value={fmt(summary.total_income)} sub={`${summary.invoice_count} invoices + bank`} icon={TrendingUp} color="green" />
        <StatCard label="Total Expenses" value={fmt(summary.total_expenses)} sub="Bank transactions" icon={TrendingDown} color="red" />
        <StatCard label="Net Cash Flow" value={fmt(summary.net_cashflow)} sub={netPositive ? "Surplus" : "Deficit"} icon={DollarSign} color={netPositive ? "sky" : "red"} />
        <StatCard label="Transactions" value={summary.transaction_count || 0} sub={`${summary.transfer_count || 0} transfers excluded`} icon={BarChart2} color="purple" />
      </div>

      {/* Income breakdown */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl p-4" style={{ border: "1px solid #334155", background: "rgba(30,41,59,0.6)" }}>
          <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 4 }}>Invoice Income</p>
          <p style={{ fontSize: 20, fontWeight: 700, color: "#34d399" }}>{fmt(summary.invoice_income)}</p>
          <p style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>Paid invoices</p>
        </div>
        <div className="rounded-xl p-4" style={{ border: "1px solid #334155", background: "rgba(30,41,59,0.6)" }}>
          <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 4 }}>Other Bank Income</p>
          <p style={{ fontSize: 20, fontWeight: 700, color: "#38bdf8" }}>{fmt(summary.bank_income)}</p>
          <p style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>Not matched to invoices</p>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Area Chart */}
        <div className="lg:col-span-2 rounded-xl p-5" style={{ border: "1px solid #334155", background: "rgba(30,41,59,0.8)" }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: "#cbd5e1", marginBottom: 16 }}>Monthly Cash Flow</h3>
          {monthly_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthly_data}>
                <defs>
                  <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f87171" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f87171" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8 }}
                  labelStyle={{ color: "#e2e8f0" }}
                  formatter={(v) => [fmt(v)]}
                />
                <Area type="monotone" dataKey="income" stroke="#34d399" fill="url(#incGrad)" strokeWidth={2} name="Income" />
                <Area type="monotone" dataKey="expenses" stroke="#f87171" fill="url(#expGrad)" strokeWidth={2} name="Expenses" />
                <Legend wrapperStyle={{ color: "#94a3b8", fontSize: 12 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState message="Import bank transactions to see monthly trends" />
          )}
        </div>

        {/* Category Pie */}
        <div className="rounded-xl p-5" style={{ border: "1px solid #334155", background: "rgba(30,41,59,0.8)" }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: "#cbd5e1", marginBottom: 16 }}>Expense Categories</h3>
          {category_breakdown.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={category_breakdown} dataKey="total" nameKey="category" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                    {category_breakdown.map((entry, i) => (
                      <Cell key={entry.category || `cat-${i}`} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8 }}
                    formatter={(v) => [fmt(v)]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-3">
                {category_breakdown.slice(0, 5).map((c, i) => (
                  <div key={c.category} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }} />
                      <span className="text-xs text-slate-400 capitalize">{c.category}</span>
                    </div>
                    <span className="text-xs text-slate-300 font-medium">{fmt(c.total)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyState message="No expense data" />
          )}
        </div>
      </div>
    </div>
  );
}

// ============= TRANSACTIONS TAB =============
function TransactionsTab({ token }) {
  const [txns, setTxns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", type: "", category: "" });
  const [editing, setEditing] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [autoCatLoading, setAutoCatLoading] = useState(false);
  const [autoCatResult, setAutoCatResult] = useState(null);

  const categories = [
    "income", "groceries", "utilities", "transport", "subscription",
    "insurance", "tax", "bank_fees", "professional", "dining",
    "entertainment", "health", "education", "uncategorized"
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filters.search) params.search = filters.search;
      if (filters.type) params.transaction_type = filters.type;
      if (filters.category) params.category = filters.category;
      const res = await axios.get(`${API}/life/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setTxns(res.data.transactions || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { load(); }, [load]);

  const saveCategory = async (txnId) => {
    try {
      await axios.put(`${API}/life/transactions/${txnId}/category`,
        { category: newCat },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Category updated");
      setEditing(null);
      load();
    } catch {
      toast.error("Failed to update category");
    }
  };

  const runAutoCategize = async () => {
    setAutoCatLoading(true);
    setAutoCatResult(null);
    try {
      const res = await axios.post(`${API}/life/auto-categorize`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutoCatResult(res.data);
      toast.success(`AI categorized ${res.data.updated} transactions`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-categorize failed. Try again.");
    } finally {
      setAutoCatLoading(false);
    }
  };

  const typeColor = {
    income: "text-emerald-400",
    expense: "text-red-400",
    transfer_in: "text-sky-400",
    transfer_out: "text-amber-400",
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="flex items-center gap-2 rounded-lg px-3 py-2 flex-1 min-w-48" style={{ background: "#1e293b", border: "1px solid #334155" }}>
          <Search className="w-4 h-4 shrink-0" style={{ color: "#94a3b8" }} />
          <input
            placeholder="Search transactions..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            style={{ background: "transparent", fontSize: 13, color: "#e2e8f0", outline: "none", width: "100%" }}
          />
        </div>
        <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
          style={{ background: "#1e293b", border: "1px solid #334155", color: "#e2e8f0", fontSize: 13, borderRadius: 8, padding: "8px 12px" }}>
          <option value="">All Types</option>
          <option value="income">Income</option>
          <option value="expense">Expense</option>
          <option value="transfer_in">Transfer In</option>
          <option value="transfer_out">Transfer Out</option>
        </select>
        <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
          style={{ background: "#1e293b", border: "1px solid #334155", color: "#e2e8f0", fontSize: 13, borderRadius: 8, padding: "8px 12px" }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Auto-categorize banner */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 10, background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Bot style={{ width: 18, height: 18, color: "#a78bfa" }} />
          <div>
            <p style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 500 }}>AI Auto-Categorizer</p>
            <p style={{ fontSize: 11, color: "#64748b" }}>Let Claude intelligently categorize all uncategorized transactions</p>
          </div>
        </div>
        <button
          onClick={runAutoCategize}
          disabled={autoCatLoading}
          data-testid="auto-categorize-btn"
          style={{ display: "flex", alignItems: "center", gap: 8, background: "#7c3aed", color: "#fff", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: autoCatLoading ? "not-allowed" : "pointer", opacity: autoCatLoading ? 0.7 : 1 }}
        >
          {autoCatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot style={{ width: 16, height: 16 }} />}
          {autoCatLoading ? "Categorizing..." : "Auto-Categorize"}
        </button>
      </div>

      {autoCatResult && (
        <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.25)" }} data-testid="autocat-result">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <CheckCircle style={{ width: 16, height: 16, color: "#34d399" }} />
            <span style={{ fontSize: 13, color: "#34d399", fontWeight: 600 }}>{autoCatResult.message}</span>
            {autoCatResult.remaining_uncategorized > 0 && (
              <span style={{ fontSize: 11, color: "#94a3b8" }}>({autoCatResult.remaining_uncategorized} more remain — run again)</span>
            )}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {Object.entries(autoCatResult.categories || {}).map(([cat, count]) => (
              <span key={cat} style={{ fontSize: 11, background: "rgba(30,41,59,0.8)", color: "#cbd5e1", borderRadius: 4, padding: "2px 8px", border: "1px solid #334155", textTransform: "capitalize" }}>
                {cat}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      <p style={{ fontSize: 12, color: "#64748b" }}>Showing {txns.length} of {total} transactions</p>

      {loading ? <LoadingSpinner /> : (
        <div className="rounded-xl overflow-hidden" style={{ border: "1px solid #334155" }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid #334155", background: "rgba(30,41,59,0.9)" }}>
                  <th style={{ textAlign: "left", fontSize: 11, color: "#94a3b8", padding: "12px 16px" }}>Date</th>
                  <th style={{ textAlign: "left", fontSize: 11, color: "#94a3b8", padding: "12px 16px" }}>Description</th>
                  <th style={{ textAlign: "left", fontSize: 11, color: "#94a3b8", padding: "12px 16px" }}>Category</th>
                  <th style={{ textAlign: "left", fontSize: 11, color: "#94a3b8", padding: "12px 16px" }}>Type</th>
                  <th style={{ textAlign: "right", fontSize: 11, color: "#94a3b8", padding: "12px 16px" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {txns.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: "center", padding: "48px 16px", color: "#64748b", background: "#0f172a" }}>No transactions found</td></tr>
                ) : txns.map(t => (
                  <tr key={t.id} style={{ borderBottom: "1px solid rgba(51,65,85,0.5)", background: "rgba(15,23,42,0.6)", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(30,41,59,0.8)"}
                    onMouseLeave={e => e.currentTarget.style.background = "rgba(15,23,42,0.6)"}>
                    <td style={{ padding: "12px 16px", color: "#94a3b8", whiteSpace: "nowrap" }}>{t.date}</td>
                    <td style={{ padding: "12px 16px", color: "#e2e8f0", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.description}>{t.description}</td>
                    <td style={{ padding: "12px 16px" }}>
                      {editing === t.id ? (
                        <div className="flex items-center gap-1">
                          <select value={newCat} onChange={e => setNewCat(e.target.value)}
                            style={{ background: "#334155", border: "1px solid #475569", color: "#e2e8f0", fontSize: 11, borderRadius: 4, padding: "2px 6px" }}>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                          <button onClick={() => saveCategory(t.id)} style={{ color: "#34d399" }}><CheckCircle className="w-4 h-4" /></button>
                          <button onClick={() => setEditing(null)} style={{ color: "#64748b" }}><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditing(t.id); setNewCat(t.category || "uncategorized"); }}
                          style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, background: "rgba(51,65,85,0.6)", color: "#cbd5e1", borderRadius: 4, padding: "2px 8px", border: "none", cursor: "pointer", textTransform: "capitalize" }}>
                          {t.category || "uncategorized"}
                        </button>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 12, fontWeight: 500, textTransform: "capitalize", color: typeColor[t.transaction_type] || "#94a3b8" }}>
                      {t.transaction_type?.replace("_", " ")}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums", color: t.amount >= 0 ? "#34d399" : "#f87171" }}>
                      {fmtDecimal(t.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============= IMPORT TAB =============
function ImportTab({ token }) {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const loadImports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/life/imports`, { headers: { Authorization: `Bearer ${token}` } });
      setImports(res.data || []);
    } catch {
      toast.error("Failed to load import history");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadImports(); }, [loadImports]);

  const handleUpload = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) {
      toast.error("Only CSV and Excel files are supported");
      return;
    }
    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/bank/smart-import`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res.data);
      toast.success(`Imported ${res.data.summary?.transactions_imported || 0} transactions`);
      loadImports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteImport = async (imp) => {
    if (!window.confirm(`Delete "${imp.filename}" and its ${imp.live_transaction_count} transactions?`)) return;
    try {
      await axios.delete(`${API}/life/imports/${imp.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Import deleted");
      loadImports();
    } catch {
      toast.error("Failed to delete import");
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload area */}
      <div
        onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files[0]); }}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        className={`rounded-xl border-2 border-dashed p-10 text-center transition-all cursor-pointer
          ${dragOver ? "border-sky-500 bg-sky-500/10" : "border-slate-600 hover:border-slate-500 bg-slate-800/30"}`}
        onClick={() => document.getElementById("lf-file-input").click()}
        data-testid="upload-drop-zone"
      >
        <input id="lf-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden"
          onChange={e => handleUpload(e.target.files[0])} />
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-sky-400 animate-spin" />
            <p className="text-slate-300 font-medium">Processing your file with AI...</p>
            <p className="text-xs text-slate-500">Auto-detecting columns, identifying transfers, checking duplicates</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload className="w-10 h-10 text-slate-500" />
            <p className="text-slate-300 font-medium">Drop your bank statement here, or click to browse</p>
            <p className="text-xs text-slate-500">Supports CSV, XLSX from CommBank, ANZ, Amex, Westpac, NAB — any format</p>
            <div className="flex gap-2 mt-2">
              {["CSV", "XLSX", "XLS"].map(f => (
                <span key={f} className="text-xs bg-slate-700 text-slate-400 px-2 py-1 rounded">{f}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Upload result */}
      {uploadResult && (
        <div className="rounded-xl p-5 space-y-3" style={{ border: "1px solid rgba(52,211,153,0.3)", background: "rgba(52,211,153,0.07)" }} data-testid="upload-result">
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">Import Successful</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xl font-bold text-white">{uploadResult.summary?.transactions_imported}</p>
              <p className="text-xs text-slate-400">Imported</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-amber-400">{uploadResult.summary?.duplicates_skipped}</p>
              <p className="text-xs text-slate-400">Duplicates Skipped</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-sky-400">{uploadResult.summary?.transfers_detected}</p>
              <p className="text-xs text-slate-400">Transfers Found</p>
            </div>
          </div>
          <div className="flex gap-4 pt-1">
            <div>
              <span className="text-xs text-slate-400">Bank Detected: </span>
              <span className="text-xs text-slate-200">{uploadResult.account?.bank}</span>
            </div>
            <div>
              <span className="text-xs text-slate-400">Income: </span>
              <span className="text-xs text-emerald-400">{fmt(uploadResult.summary?.income_total)}</span>
            </div>
            <div>
              <span className="text-xs text-slate-400">Expenses: </span>
              <span className="text-xs text-red-400">{fmt(uploadResult.summary?.expense_total)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Import history */}
      <div>
        <h3 className="text-sm font-semibold text-slate-300 mb-3">Uploaded Files</h3>
        {loading ? <LoadingSpinner /> : imports.length === 0 ? (
          <div className="rounded-xl border border-slate-700 p-8 text-center text-slate-500">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No files uploaded yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {imports.map(imp => (
              <div key={imp.id} className="flex items-center justify-between rounded-lg px-4 py-3" style={{ border: "1px solid #334155", background: "rgba(30,41,59,0.6)" }} data-testid={`import-row-${imp.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-5 h-5 text-sky-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-slate-200 font-medium truncate">{imp.filename}</p>
                    <p className="text-xs text-slate-500">
                      {imp.bank_detected} · {imp.live_transaction_count} transactions · {imp.imported_at?.slice(0, 10)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-4">
                  <span className="text-xs text-emerald-400 font-medium">{imp.transactions_imported} new</span>
                  {imp.duplicates_skipped > 0 && (
                    <span className="text-xs text-amber-400">{imp.duplicates_skipped} dupe</span>
                  )}
                  <button
                    onClick={() => deleteImport(imp)}
                    className="text-slate-500 hover:text-red-400 transition-colors p-1 rounded"
                    data-testid={`delete-import-${imp.id}`}
                    title="Delete this import and all its transactions"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============= BAS ADVISOR TAB =============
function BASAdvisorTab({ token }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fy, setFy] = useState("2024-25");

  const fyOptions = ["2021-22", "2022-23", "2023-24", "2024-25", "2025-26"];

  const runAnalysis = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/life/bas-advisor`,
        { financial_year: fy },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Analysis failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header + Run control */}
      <div className="rounded-xl p-6" style={{ border: "1px solid #334155", background: "rgba(30,41,59,0.8)" }}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
            <Bot className="w-5 h-5 text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-white">BAS & Tax Advisor</h3>
            <p className="text-sm text-slate-400 mt-1">
              AI-powered Australian tax analysis using live invoice and bank data.
              Estimates income tax, GST liability, and provides ATO compliance advice.
            </p>
            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <select value={fy} onChange={e => setFy(e.target.value)}
                style={{ background: "#334155", border: "1px solid #475569", color: "#e2e8f0", fontSize: 13, borderRadius: 8, padding: "8px 12px" }}>
                {fyOptions.map(f => <option key={f} value={f}>FY {f}</option>)}
              </select>
              <button
                onClick={runAnalysis}
                disabled={loading}
                data-testid="run-bas-btn"
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
                {loading ? "Analysing..." : "Run Analysis"}
              </button>
              <a
                href="/finance-advisor"
                data-testid="open-finance-advisor-link"
                className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Open Finance Advisor →
              </a>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex flex-col items-center gap-4 py-12">
          <Loader2 className="w-10 h-10 text-purple-400 animate-spin" />
          <p className="text-slate-300">Claude is analysing your financials...</p>
          <p className="text-xs text-slate-500">Combining invoices + bank data with ATO rules. This may take 15–30 seconds.</p>
        </div>
      )}

      {result && (
        <div className="space-y-5" data-testid="bas-result">
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Income" value={fmt(result.income_summary?.total_income)} sub={`${result.income_summary?.invoice_count} invoices`} icon={TrendingUp} color="green" />
            <StatCard label="Total Expenses" value={fmt(result.expense_summary?.total_expenses)} sub="Bank transactions" icon={TrendingDown} color="red" />
            <StatCard label="Est. Tax Payable" value={fmt(result.tax_calculation?.net_tax_payable)} sub={`${result.tax_calculation?.effective_rate_pct}% effective rate`} icon={Receipt} color="amber" />
            <StatCard label="GST Payable" value={fmtDecimal(result.gst_calculation?.net_gst_payable)} sub={result.gst_calculation?.must_register_for_gst ? "Must register" : "Below threshold"} icon={DollarSign} color="purple" />
          </div>

          {/* Tax breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="rounded-xl p-5" style={{ border: "1px solid #334155", background: "rgba(30,41,59,0.8)" }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: "#cbd5e1", marginBottom: 16 }}>Income Tax Breakdown (ATO 2024-25)</h4>
              <div className="space-y-2">
                {[
                  { label: "Gross Taxable Income", val: fmt(result.taxable_income), cls: "text-white" },
                  { label: "Gross Income Tax", val: fmt(result.tax_calculation?.gross_tax), cls: "text-amber-400" },
                  { label: "LITO Offset", val: `-${fmt(result.tax_calculation?.lito_offset)}`, cls: "text-emerald-400" },
                  { label: "Medicare Levy (2%)", val: fmt(result.tax_calculation?.medicare_levy), cls: "text-amber-400" },
                  { label: "NET TAX PAYABLE", val: fmt(result.tax_calculation?.net_tax_payable), cls: "text-red-400 font-bold" },
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-center py-1.5 border-b border-slate-700/50 last:border-0">
                    <span className="text-sm text-slate-400">{row.label}</span>
                    <span className={`text-sm font-medium ${row.cls}`}>{row.val}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl p-5" style={{ border: "1px solid #334155", background: "rgba(30,41,59,0.8)" }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: "#cbd5e1", marginBottom: 16 }}>GST / BAS Position</h4>
              <div className="space-y-2">
                {[
                  { label: "GST Collected", val: fmtDecimal(result.gst_calculation?.gst_collected), cls: "text-amber-400" },
                  { label: "GST Input Credits", val: `-${fmtDecimal(result.gst_calculation?.gst_credits)}`, cls: "text-emerald-400" },
                  { label: "NET GST PAYABLE", val: fmtDecimal(result.gst_calculation?.net_gst_payable), cls: "text-red-400 font-bold" },
                  { label: "Registration Required", val: result.gst_calculation?.must_register_for_gst ? "YES — Register now" : "NO — Under $75k", cls: result.gst_calculation?.must_register_for_gst ? "text-red-400" : "text-emerald-400" },
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-center py-1.5 border-b border-slate-700/50 last:border-0">
                    <span className="text-sm text-slate-400">{row.label}</span>
                    <span className={`text-sm font-medium ${row.cls}`}>{row.val}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-sky-500/10 border border-sky-500/20">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-400">
                    Income is cross-referenced between invoices and bank transactions to prevent double-counting.
                    Deductible expenses reduce your taxable income.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Expense categories */}
          {result.expense_summary?.expense_categories?.length > 0 && (
            <div className="rounded-xl p-5" style={{ border: "1px solid #334155", background: "rgba(30,41,59,0.8)" }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: "#cbd5e1", marginBottom: 16 }}>Expense Categories</h4>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {result.expense_summary.expense_categories.slice(0, 9).map((c, i) => (
                  <div key={c.category} className="flex items-center justify-between rounded-lg bg-slate-700/40 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }} />
                      <span className="text-xs text-slate-400 capitalize">{c.category}</span>
                    </div>
                    <span className="text-xs text-slate-300 font-medium">{fmt(c.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Advice */}
          <div className="rounded-xl p-6" style={{ border: "1px solid rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.05)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Bot className="w-5 h-5 text-purple-400" />
              <h4 className="text-sm font-semibold text-purple-300">Claude AI Tax Advice — FY {result.financial_year}</h4>
            </div>
            <div className="prose prose-invert prose-sm max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-slate-300 font-sans leading-relaxed">
                {result.ai_advice}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============= SHARED COMPONENTS =============
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex items-center justify-center h-32 text-slate-500 text-sm">{message}</div>
  );
}

// ============= MAIN PAGE =============
export default function LifeFinancePage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <DashboardLayout title="Life Finance">
      {/* Dark overlay wrapping all content */}
      <div style={{ margin: "-2rem -2rem -2rem", padding: "2rem", background: "#0f172a", minHeight: "calc(100vh - 64px)" }}>
        {/* Page header */}
        <div className="mb-6">
          <p style={{ color: "#94a3b8" }} className="text-sm">Your unified financial life — business, personal, and investments in one place.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 w-fit" style={{ background: "#1e293b", borderRadius: "12px", padding: "4px", border: "1px solid #334155" }}>
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              data-testid={`tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}
              style={{
                padding: "8px 20px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 500,
                transition: "all 0.15s",
                background: activeTab === i ? "#334155" : "transparent",
                color: activeTab === i ? "#f1f5f9" : "#94a3b8",
                border: "none",
                cursor: "pointer",
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div>
          {activeTab === 0 && <OverviewTab token={token} />}
          {activeTab === 1 && <TransactionsTab token={token} />}
          {activeTab === 2 && <ImportTab token={token} />}
          {activeTab === 3 && <BASAdvisorTab token={token} />}
        </div>
      </div>
    </DashboardLayout>
  );
}
